import { FC, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ChakraProvider } from '@chakra-ui/react';
import { system } from './theme';
import { useAuthStore } from './stores/authStore';
import { useVersionCheck } from './hooks/useVersionCheck';
import { Toaster } from './components/ui/toaster';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { AdminRoute } from './components/auth/AdminRoute';
import LoginPage from './pages/LoginPage';
import DogListPage from './pages/DogListPage';
import DogDetailPage from './pages/DogDetailPage';
import DogFormPage from './pages/DogFormPage';
import BirthRecordListPage from './pages/BirthRecordListPage';
import BirthRecordFormPage from './pages/BirthRecordFormPage';
import MatingRecordListPage from './pages/MatingRecordListPage';
import MatingRecordFormPage from './pages/MatingRecordFormPage';
import PedigreeListPage from './pages/PedigreeListPage';
import AIMatingProposalPage from './pages/AIMatingProposalPage';
import AIPromptListPage from './pages/admin/AIPromptListPage';
import AIPromptEditPage from './pages/admin/AIPromptEditPage';
import AIAnalysisHistoryPage from './pages/admin/AIAnalysisHistoryPage';
import AIAnalysisHistoryDetailPage from './pages/admin/AIAnalysisHistoryDetailPage';
import AccountListPage from './pages/admin/AccountListPage';
import AccountFormPage from './pages/admin/AccountFormPage';
import BusinessPage from './pages/admin/BusinessPage';
import ManualPage from './pages/ManualPage';

/**
 * ルートアプリケーションコンポーネント
 */
const App: FC = () => {
  const { initAuth } = useAuthStore();

  // アプリ起動時に認証状態を初期化
  useEffect(() => {
    initAuth();
  }, [initAuth]);

  // バージョンチェック（アプリ起動時に実行、新バージョン検出で自動更新）
  useVersionCheck();

  return (
    <ChakraProvider value={system}>
      <Toaster />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/" element={<Navigate to="/dogs" replace />} />
          <Route
            path="/dogs"
            element={
              <ProtectedRoute>
                <DogListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dogs/new"
            element={
              <ProtectedRoute>
                <DogFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dogs/:id"
            element={
              <ProtectedRoute>
                <DogDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dogs/:id/edit"
            element={
              <ProtectedRoute>
                <DogFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/birth-records"
            element={
              <ProtectedRoute>
                <BirthRecordListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/birth-records/new"
            element={
              <ProtectedRoute>
                <BirthRecordFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/birth-records/:id/edit"
            element={
              <ProtectedRoute>
                <BirthRecordFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/mating-records"
            element={
              <ProtectedRoute>
                <MatingRecordListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/mating-records/new"
            element={
              <ProtectedRoute>
                <MatingRecordFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/mating-records/:id/edit"
            element={
              <ProtectedRoute>
                <MatingRecordFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pedigree"
            element={
              <ProtectedRoute>
                <PedigreeListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ai-mating"
            element={
              <ProtectedRoute>
                <AIMatingProposalPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/prompts"
            element={
              <ProtectedRoute>
                <AIPromptListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/prompts/new"
            element={
              <ProtectedRoute>
                <AIPromptEditPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/prompts/:id"
            element={
              <ProtectedRoute>
                <AIPromptEditPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/analysis-history"
            element={
              <ProtectedRoute>
                <AIAnalysisHistoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/analysis-history/:id"
            element={
              <ProtectedRoute>
                <AIAnalysisHistoryDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/accounts"
            element={
              <AdminRoute>
                <AccountListPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/accounts/new"
            element={
              <AdminRoute>
                <AccountFormPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/accounts/:uid/edit"
            element={
              <AdminRoute>
                <AccountFormPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/business"
            element={
              <AdminRoute>
                <BusinessPage />
              </AdminRoute>
            }
          />
          <Route
            path="/manual"
            element={
              <ProtectedRoute>
                <ManualPage />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
    </ChakraProvider>
  );
};

export default App;
